import React from "react"

import { useForm } from "react-hook-form"
import { Link } from "react-router-dom"

import { AuthFlowRoute } from "@common-shared/config/auth.config"
import type { IAuthFlowData } from "@common-shared/types/auth.types.ts"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { LoginStep } from "../../../../config/auth.config"
import { ROUTES_CONFIG } from "../../../../config/routes.config.ts"
import { AuthService } from "../../../../services/auth.service.ts"
import { useAppDispatch } from "../../../../store/appDispatch.hook.ts"
import { useAppSelector } from "../../../../store/selectors/app.selector.ts"
import { authSelector } from "../../../../store/selectors/auth.selector.ts"
import {
    authSetLoginEmail,
    authSetLoginStep,
} from "../../../../store/slices/auth.slice.ts"
import { authCheckIdentityProvider } from "../../../../store/thunks/auth.thunks.ts"
import { IEmailData } from "../../../../types/data.types.ts"

import arrowRightIcon from "@front-shared/assets/images/icons/arrow-right2.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"
import mainImage from "@front-shared/assets/images/shapes/login.svg"

export const EmailStep: React.FunctionComponent = () => {
    const dispatch = useAppDispatch()

    const {
        handleSubmit,
        formState: { errors },
        control,
    } = useForm<IEmailData>()

    const authState = useAppSelector(authSelector)

    const checkForIdentityProvider = (email: string) => {
        dispatch(
            authCheckIdentityProvider({
                payload: email,
                onSuccess: async (response: IAuthFlowData) => {
                    if (
                        response.route === AuthFlowRoute.SSO &&
                        response.providerName
                    ) {
                        // Automatically redirect to SSO
                        try {
                            await AuthService.federatedSignInWithProvider(
                                response.providerName,
                            )
                        } catch (error) {
                            console.error(
                                "Federated sign-in with provider failed:",
                                error,
                            )
                        }
                    } else if (response.route === AuthFlowRoute.PASSWORD) {
                        // Continue to password step
                        dispatch(authSetLoginEmail(email))
                        dispatch(authSetLoginStep(LoginStep.PASSWORD))
                    }
                },
            }),
        )
    }

    return (
        <div className="relative">
            <img
                src={mainImage}
                alt="Nirvana"
                title="Nirvana"
                className="margin-x-center sm:w-[150px]"
            />

            <PrimaryText className="mt-[32px]" typography="h1" centered>
                Welcome back to <br />
                Nirvana
            </PrimaryText>

            <PrimaryText typography="subtitle" centered className="mt-[32px]">
                Insurance coverage solutions optimized for your specialty and
                scale.
            </PrimaryText>

            <form
                onSubmit={handleSubmit(data => {
                    checkForIdentityProvider(data.email)
                })}
                noValidate={true}
            >
                <div className="mt-[44px] flex items-start md:block">
                    <InputElement
                        className="flex-1"
                        name="email"
                        label="Email address"
                        reactHookControl={control as any}
                        reactHookErrors={errors}
                        reactHookValidations={{
                            required: FRONT_VALIDATION_CONFIG.required,
                            pattern: FRONT_VALIDATION_CONFIG.email,
                            maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                        }}
                        inputSuffix={
                            <img
                                src={mailIcon}
                                alt="Email address"
                                title="Email address"
                                className="ml-[8px]"
                            />
                        }
                        defaultValue={
                            authState.loginEmail || authState.loginData?.email
                        }
                    />
                </div>

                <div className="mt-[32px] flex items-center">
                    <div className="flex-1 mr-[16px]">
                        <Link to={ROUTES_CONFIG.restoreUrl}>
                            Forgot password?
                        </Link>
                    </div>

                    <ButtonElement
                        label="Continue"
                        type="primary"
                        htmlType="submit"
                        size="large"
                        icon={arrowRightIcon}
                        isRightIcon
                        isLoading={authState.isLoading}
                    />
                </div>
            </form>
        </div>
    )
}
